import { TaskStatus } from '@/services/uploaderService';

import { useTracker } from '@/core/tracking';
import { ImageSelectorToolbar } from '@/features/imageEditor/imageSelectorToolbar';
import { useCallback, useState } from 'react';
import { mediaShownInComparerState } from '@/stores/mediaStore';
import { useRecoilValue } from 'recoil';
import Comparer from './Comparer';
import mediaDisplayTracker from '../mediaDisplayTracker/mediaDisplayTacker';

function ComparerArea({
  hjSuppress = false,
  showTooltip = false,
  isLoading,
  isImageSelectorToolbarVisible,
  className,
  fromId,
  ...rest
}: {
  hjSuppress?: boolean;
  showTooltip?: boolean;
  isLoading?: boolean;
  className?: string;
  isImageSelectorToolbarVisible?: boolean;
  fromId: 'share' | 'result';
}) {
  const { track } = useTracker();
  const selectedMedia = useRecoilValue(mediaShownInComparerState);

  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const onSlide = useCallback(() => {
    if (isFirstSlide) {
      track('editor', 'slider_interaction');
      setIsFirstSlide(false);
    }
  }, [track, isFirstSlide]);

  const [isFirstPan, setIsFirstPan] = useState(true);
  const onPan = useCallback(() => {
    if (isFirstPan) {
      track('editor', 'panned');
      setIsFirstPan(false);
    }
  }, [track, isFirstPan]);

  const [isFirstZoom, setIsFirstZoom] = useState(true);
  const onZoom = useCallback(() => {
    if (isFirstZoom) {
      track('editor', 'zoomed');
      setIsFirstZoom(false);
    }
  }, [track, isFirstZoom]);

  const finalOutputUrl = selectedMedia.convertedImages?.length
    ? selectedMedia.convertedImages[0].outputUri
    : selectedMedia?.outputUrl;

  const onLoadMediaStart = useCallback(() => {
    mediaDisplayTracker.setInfo({
      media_type: 'image',
      from_id: fromId,
      has_watermark: Boolean(selectedMedia.hasWatermark),
    });
    mediaDisplayTracker.start();
  }, [fromId, selectedMedia]);

  const onLoadMediaComplete = useCallback(() => {
    mediaDisplayTracker.complete();
  }, []);

  const onLoadMediaFail = useCallback((e: unknown) => {
    // the if is needed to prevent double fail due to the double texture rendering (before and after)
    if (!mediaDisplayTracker.isFailed()) {
      mediaDisplayTracker.fail(e);
    }
  }, []);

  // Do not settle for the default values
  if (!selectedMedia?.inputUrl || !selectedMedia.outputUrl) {
    // TODO: sensible placeholder
    return null;
  }

  return (
    <div
      className={`relative flex-grow overflow-hidden ${
        window.location.pathname !== '/result' ? '' : 'rounded-[16px]'
      }`}
    >
      <Comparer
        backgroundColor={0x000}
        initialFit="contain"
        beforeSrc={selectedMedia.inputUrl}
        afterSrc={
          selectedMedia?.status === TaskStatus.Processing
            ? selectedMedia?.inputUrl
            : finalOutputUrl!
        }
        badgesAlignment="top"
        onPan={onPan}
        onSlide={onSlide}
        onZoom={onZoom}
        hjSuppress={hjSuppress}
        showTooltip={showTooltip}
        isLoading={isLoading}
        sliderBehavior="default"
        onLoadStart={onLoadMediaStart}
        onLoadComplete={onLoadMediaComplete}
        onLoadFail={onLoadMediaFail}
        {...rest}
      />
      {isImageSelectorToolbarVisible && <ImageSelectorToolbar />}
    </div>
  );
}

export default ComparerArea;
